import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Divider,
    Tabs,
    Tab,
    Grid,
} from '@mui/material';
import {
    Save as SaveIcon,
    Upload as UploadIcon,
    Refresh as RefreshIcon,
    Palette as PaletteIcon,
    Image as ImageIcon,
    Link as LinkIcon,
} from '@mui/icons-material';
import { Title, useGetIdentity, useNotify } from 'react-admin';
import { brandingService, Branding } from '../../services/brandingService';

const BrandingSettings: React.FC = () => {
    const { data: identity, isLoading: identityLoading } = useGetIdentity();
    const notify = useNotify();

    const [branding, setBranding] = useState<Branding | null>(null);
    const [companyName, setCompanyName] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [uploadingFavicon, setUploadingFavicon] = useState(false);
    const [uploadingBackground, setUploadingBackground] = useState(false);

    // Form state
    const [domain, setDomain] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [primaryColor, setPrimaryColor] = useState('#1976d2');
    const [secondaryColor, setSecondaryColor] = useState('#dc004e');
    const [faviconUrl, setFaviconUrl] = useState('');
    const [loginBackgroundUrl, setLoginBackgroundUrl] = useState('');
    const [appTitle, setAppTitle] = useState('');
    const [poweredByText, setPoweredByText] = useState('');

    // Tab state for input types
    const [logoInputType, setLogoInputType] = useState<'upload' | 'url'>('upload');
    const [faviconInputType, setFaviconInputType] = useState<'upload' | 'url'>('upload');
    const [backgroundInputType, setBackgroundInputType] = useState<'upload' | 'url'>('upload');

    const logoFileInputRef = useRef<HTMLInputElement>(null);
    const faviconFileInputRef = useRef<HTMLInputElement>(null);
    const backgroundFileInputRef = useRef<HTMLInputElement>(null);

    // Load branding on mount
    useEffect(() => {
        const loadBranding = async () => {
            if (!identity?.companyId) return;

            setLoading(true);
            try {
                const response = await brandingService.getBrandingByCompanyId(identity.companyId, localStorage.getItem('token') || '');
                setBranding(response.branding);
                setCompanyName(response.companyName);

                // Populate form
                setDomain(response.branding.domain || '');
                setLogoUrl(response.branding.logoUrl || '');
                setPrimaryColor(response.branding.primaryColor || '#1976d2');
                setSecondaryColor(response.branding.secondaryColor || '#dc004e');
                setFaviconUrl(response.branding.faviconUrl || '');
                setLoginBackgroundUrl(response.branding.loginBackgroundUrl || '');
                setAppTitle(response.branding.appTitle || '');
                setPoweredByText(response.branding.poweredByText || '');
            } catch (error) {
                notify('Fehler beim Laden des Brandings', { type: 'error' });
            } finally {
                setLoading(false);
            }
        };

        if (!identityLoading) {
            loadBranding();
        }
    }, [identity?.companyId, identityLoading, notify]);

    const handleSave = async () => {
        if (!identity?.companyId) return;

        setSaving(true);
        try {
            const result = await brandingService.updateBranding(identity.companyId, {
                domain: domain || null,
                logoUrl: logoUrl || null,
                primaryColor: primaryColor || '#1976d2',
                secondaryColor: secondaryColor || '#dc004e',
                faviconUrl: faviconUrl || null,
                loginBackgroundUrl: loginBackgroundUrl || null,
                appTitle: appTitle || null,
                poweredByText: poweredByText || null,
            });

            if (result.success) {
                setBranding(result.branding!);
                notify('Branding erfolgreich gespeichert', { type: 'success' });
            } else {
                notify(result.error || 'Fehler beim Speichern', { type: 'error' });
            }
        } catch (error) {
            notify('Fehler beim Speichern des Brandings', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !identity?.companyId) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            notify('Ungültiges Dateiformat. Erlaubt: JPG, PNG, GIF, SVG, WebP', { type: 'error' });
            return;
        }

        // Validate file size (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            notify('Datei zu groß. Maximal 2MB erlaubt.', { type: 'error' });
            return;
        }

        setUploadingLogo(true);
        try {
            const result = await brandingService.uploadLogo(identity.companyId, file);
            if (result.success && result.logoUrl) {
                setLogoUrl(result.logoUrl);
                notify('Logo erfolgreich hochgeladen', { type: 'success' });
            } else {
                notify(result.error || 'Fehler beim Hochladen', { type: 'error' });
            }
        } catch (error) {
            notify('Fehler beim Hochladen des Logos', { type: 'error' });
        } finally {
            setUploadingLogo(false);
            // Reset file input
            if (logoFileInputRef.current) {
                logoFileInputRef.current.value = '';
            }
        }
    };

    const handleFaviconUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !identity?.companyId) return;

        // Validate file type
        const allowedTypes = ['image/x-icon', 'image/vnd.microsoft.icon', 'image/png', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
            notify('Ungültiges Dateiformat. Erlaubt: ICO, PNG, SVG', { type: 'error' });
            return;
        }

        // Validate file size (max 500KB)
        if (file.size > 500 * 1024) {
            notify('Datei zu groß. Maximal 500KB erlaubt.', { type: 'error' });
            return;
        }

        setUploadingFavicon(true);
        try {
            const result = await brandingService.uploadFavicon(identity.companyId, file);
            if (result.success && result.faviconUrl) {
                setFaviconUrl(result.faviconUrl);
                notify('Favicon erfolgreich hochgeladen', { type: 'success' });
            } else {
                notify(result.error || 'Fehler beim Hochladen', { type: 'error' });
            }
        } catch (error) {
            notify('Fehler beim Hochladen des Favicons', { type: 'error' });
        } finally {
            setUploadingFavicon(false);
            if (faviconFileInputRef.current) {
                faviconFileInputRef.current.value = '';
            }
        }
    };

    const handleBackgroundUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !identity?.companyId) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            notify('Ungültiges Dateiformat. Erlaubt: JPG, PNG, WebP', { type: 'error' });
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            notify('Datei zu groß. Maximal 5MB erlaubt.', { type: 'error' });
            return;
        }

        setUploadingBackground(true);
        try {
            const result = await brandingService.uploadBackground(identity.companyId, file);
            if (result.success && result.backgroundUrl) {
                setLoginBackgroundUrl(result.backgroundUrl);
                notify('Hintergrundbild erfolgreich hochgeladen', { type: 'success' });
            } else {
                notify(result.error || 'Fehler beim Hochladen', { type: 'error' });
            }
        } catch (error) {
            notify('Fehler beim Hochladen des Hintergrundbildes', { type: 'error' });
        } finally {
            setUploadingBackground(false);
            if (backgroundFileInputRef.current) {
                backgroundFileInputRef.current.value = '';
            }
        }
    };

    const handleReset = () => {
        if (branding) {
            setDomain(branding.domain || '');
            setLogoUrl(branding.logoUrl || '');
            setPrimaryColor(branding.primaryColor || '#1976d2');
            setSecondaryColor(branding.secondaryColor || '#dc004e');
            setFaviconUrl(branding.faviconUrl || '');
            setLoginBackgroundUrl(branding.loginBackgroundUrl || '');
            setAppTitle(branding.appTitle || '');
            setPoweredByText(branding.poweredByText || '');
        }
    };

    if (identityLoading || loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!identity?.companyId) {
        return (
            <Box sx={{ padding: 3 }}>
                <Alert severity="error">Keine Firma zugeordnet. Branding kann nicht konfiguriert werden.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ padding: 3, maxWidth: 1200, margin: '0 auto' }}>
            <Title title="Branding-Einstellungen" />

            <Typography variant="h4" sx={{ marginBottom: 1, fontWeight: 700, color: '#1f3240' }}>
                Branding-Einstellungen
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ marginBottom: 3 }}>
                Passen Sie das Erscheinungsbild Ihrer Online Academy an Ihre Corporate Identity an.
            </Typography>

            <Grid container spacing={3}>
                {/* Left Column - Settings */}
                <Grid size={{ xs: 12, md: 7 }}>
                    <Paper sx={{ borderRadius: '12px', p: 3 }}>
                        {/* Domain */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinkIcon /> Domain-Zuordnung
                            </Typography>
                            <TextField
                                fullWidth
                                label="Domain"
                                value={domain}
                                onChange={(e) => setDomain(e.target.value)}
                                placeholder="z.B. academy.ihre-firma.de"
                                helperText="Die Domain, unter der Ihr Branding angezeigt werden soll (ohne https://)"
                            />
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        {/* Logo */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ImageIcon /> Logo
                            </Typography>

                            <Tabs
                                value={logoInputType}
                                onChange={(e, v) => setLogoInputType(v)}
                                sx={{ mb: 2 }}
                            >
                                <Tab value="upload" label="Hochladen" icon={<UploadIcon />} iconPosition="start" />
                                <Tab value="url" label="Externe URL" icon={<LinkIcon />} iconPosition="start" />
                            </Tabs>

                            {logoInputType === 'upload' ? (
                                <Box>
                                    <input
                                        ref={logoFileInputRef}
                                        type="file"
                                        accept="image/jpeg,image/png,image/gif,image/svg+xml,image/webp"
                                        style={{ display: 'none' }}
                                        onChange={handleLogoUpload}
                                    />
                                    <Button
                                        variant="outlined"
                                        startIcon={uploadingLogo ? <CircularProgress size={20} /> : <UploadIcon />}
                                        onClick={() => logoFileInputRef.current?.click()}
                                        disabled={uploadingLogo}
                                    >
                                        {uploadingLogo ? 'Wird hochgeladen...' : 'Logo hochladen'}
                                    </Button>
                                    <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                                        Erlaubte Formate: JPG, PNG, GIF, SVG, WebP. Maximale Größe: 2MB
                                    </Typography>
                                </Box>
                            ) : (
                                <TextField
                                    fullWidth
                                    label="Logo URL"
                                    value={logoUrl}
                                    onChange={(e) => setLogoUrl(e.target.value)}
                                    placeholder="https://ihre-firma.de/logo.png"
                                    helperText="Vollständige URL zu Ihrem Logo"
                                />
                            )}

                            {logoUrl && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="caption" color="text.secondary">Aktuelles Logo:</Typography>
                                    <TextField
                                        fullWidth
                                        value={logoUrl}
                                        onChange={(e) => setLogoUrl(e.target.value)}
                                        size="small"
                                        sx={{ mt: 1 }}
                                    />
                                </Box>
                            )}
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        {/* Colors */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PaletteIcon /> Farben
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid size={6}>
                                    <Typography variant="body2" sx={{ mb: 1 }}>Primärfarbe</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <input
                                            type="color"
                                            value={primaryColor}
                                            onChange={(e) => setPrimaryColor(e.target.value)}
                                            style={{
                                                width: 50,
                                                height: 40,
                                                border: '1px solid #ccc',
                                                borderRadius: 4,
                                                cursor: 'pointer',
                                            }}
                                        />
                                        <TextField
                                            size="small"
                                            value={primaryColor}
                                            onChange={(e) => setPrimaryColor(e.target.value)}
                                            sx={{ flex: 1 }}
                                        />
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Für Buttons, Links, Akzente
                                    </Typography>
                                </Grid>
                                <Grid size={6}>
                                    <Typography variant="body2" sx={{ mb: 1 }}>Sekundärfarbe</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <input
                                            type="color"
                                            value={secondaryColor}
                                            onChange={(e) => setSecondaryColor(e.target.value)}
                                            style={{
                                                width: 50,
                                                height: 40,
                                                border: '1px solid #ccc',
                                                borderRadius: 4,
                                                cursor: 'pointer',
                                            }}
                                        />
                                        <TextField
                                            size="small"
                                            value={secondaryColor}
                                            onChange={(e) => setSecondaryColor(e.target.value)}
                                            sx={{ flex: 1 }}
                                        />
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Für Erfolg, Highlights
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        {/* Favicon */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ImageIcon /> Favicon
                            </Typography>

                            <Tabs
                                value={faviconInputType}
                                onChange={(e, v) => setFaviconInputType(v)}
                                sx={{ mb: 2 }}
                            >
                                <Tab value="upload" label="Hochladen" icon={<UploadIcon />} iconPosition="start" />
                                <Tab value="url" label="Externe URL" icon={<LinkIcon />} iconPosition="start" />
                            </Tabs>

                            {faviconInputType === 'upload' ? (
                                <Box>
                                    <input
                                        ref={faviconFileInputRef}
                                        type="file"
                                        accept="image/x-icon,image/png,image/svg+xml,.ico"
                                        style={{ display: 'none' }}
                                        onChange={handleFaviconUpload}
                                    />
                                    <Button
                                        variant="outlined"
                                        startIcon={uploadingFavicon ? <CircularProgress size={20} /> : <UploadIcon />}
                                        onClick={() => faviconFileInputRef.current?.click()}
                                        disabled={uploadingFavicon}
                                    >
                                        {uploadingFavicon ? 'Wird hochgeladen...' : 'Favicon hochladen'}
                                    </Button>
                                    <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                                        Erlaubte Formate: ICO, PNG, SVG. Maximale Größe: 500KB
                                    </Typography>
                                </Box>
                            ) : (
                                <TextField
                                    fullWidth
                                    label="Favicon URL"
                                    value={faviconUrl}
                                    onChange={(e) => setFaviconUrl(e.target.value)}
                                    placeholder="https://ihre-firma.de/favicon.ico"
                                    helperText="Vollständige URL zu Ihrem Favicon"
                                />
                            )}

                            {faviconUrl && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="caption" color="text.secondary">Aktuelles Favicon:</Typography>
                                    <TextField
                                        fullWidth
                                        value={faviconUrl}
                                        onChange={(e) => setFaviconUrl(e.target.value)}
                                        size="small"
                                        sx={{ mt: 1 }}
                                    />
                                </Box>
                            )}
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        {/* Login Background */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ImageIcon /> Login-Hintergrund
                            </Typography>

                            <Tabs
                                value={backgroundInputType}
                                onChange={(e, v) => setBackgroundInputType(v)}
                                sx={{ mb: 2 }}
                            >
                                <Tab value="upload" label="Hochladen" icon={<UploadIcon />} iconPosition="start" />
                                <Tab value="url" label="Externe URL" icon={<LinkIcon />} iconPosition="start" />
                            </Tabs>

                            {backgroundInputType === 'upload' ? (
                                <Box>
                                    <input
                                        ref={backgroundFileInputRef}
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        style={{ display: 'none' }}
                                        onChange={handleBackgroundUpload}
                                    />
                                    <Button
                                        variant="outlined"
                                        startIcon={uploadingBackground ? <CircularProgress size={20} /> : <UploadIcon />}
                                        onClick={() => backgroundFileInputRef.current?.click()}
                                        disabled={uploadingBackground}
                                    >
                                        {uploadingBackground ? 'Wird hochgeladen...' : 'Hintergrund hochladen'}
                                    </Button>
                                    <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                                        Erlaubte Formate: JPG, PNG, WebP. Maximale Größe: 5MB. Empfohlen: 1920x1080
                                    </Typography>
                                </Box>
                            ) : (
                                <TextField
                                    fullWidth
                                    label="Hintergrundbild URL"
                                    value={loginBackgroundUrl}
                                    onChange={(e) => setLoginBackgroundUrl(e.target.value)}
                                    placeholder="https://ihre-firma.de/background.jpg"
                                    helperText="Vollständige URL zu Ihrem Hintergrundbild"
                                />
                            )}

                            {loginBackgroundUrl && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="caption" color="text.secondary">Aktueller Hintergrund:</Typography>
                                    <TextField
                                        fullWidth
                                        value={loginBackgroundUrl}
                                        onChange={(e) => setLoginBackgroundUrl(e.target.value)}
                                        size="small"
                                        sx={{ mt: 1 }}
                                    />
                                </Box>
                            )}
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        {/* App Title & Powered By */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                Titel & Texte
                            </Typography>

                            <TextField
                                fullWidth
                                label="App-Titel"
                                value={appTitle}
                                onChange={(e) => setAppTitle(e.target.value)}
                                placeholder="Online Academy"
                                helperText="Wird als Browser-Tab-Titel angezeigt. Leer = Unternehmensname."
                                sx={{ mb: 2 }}
                            />

                            <TextField
                                fullWidth
                                label="Powered by-Text"
                                value={poweredByText}
                                onChange={(e) => setPoweredByText(e.target.value)}
                                placeholder="online:academy"
                                helperText='Wird unten in der Navigation als "powered by ..." angezeigt. Leer = "online:academy".'
                            />
                        </Box>

                        {/* Actions */}
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={handleReset}
                            >
                                Zurücksetzen
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Wird gespeichert...' : 'Speichern'}
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

                {/* Right Column - Preview */}
                <Grid size={{ xs: 12, md: 5 }}>
                    <Paper sx={{ borderRadius: '12px', p: 3, position: 'sticky', top: 100 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Vorschau</Typography>

                        {/* Logo Preview */}
                        <Box
                            sx={{
                                bgcolor: primaryColor,
                                color: '#fff',
                                borderRadius: '12px',
                                height: 72,
                                px: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                            }}
                        >
                            <img
                                src={logoUrl ? (logoUrl.startsWith('/') ? `${process.env.REACT_APP_SSO_URL}${logoUrl}` : logoUrl) : '/images/top-bar-logo.png'}
                                alt="Logo Vorschau"
                                style={{ height: 30, maxWidth: 200, minWidth: 0, objectFit: 'contain' }}
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = '';
                                    (e.target as HTMLImageElement).alt = 'Logo nicht gefunden';
                                }}
                            />
                            <Box
                                component="span"
                                sx={{ bgcolor: secondaryColor, borderRadius: 999, px: 2.5, py: 1.25, fontSize: '.92rem', fontWeight: 600, flex: 'none' }}
                            >
                                Ausloggen
                            </Box>
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, mb: 2 }}>
                            {logoUrl
                                ? 'So erscheinen Logo und Farben in der oberen Leiste.'
                                : 'Ohne eigenes Logo erscheint das online:academy-Logo in der oberen Leiste.'}
                        </Typography>

                        {/* Color Preview */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>Farbvorschau:</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="contained"
                                    color="inherit"
                                    sx={{
                                        color: '#fff',
                                        backgroundColor: `${primaryColor} !important`,
                                        '&:hover': { backgroundColor: `${primaryColor} !important`, opacity: 0.9 },
                                    }}
                                >
                                    Primär
                                </Button>
                                <Button
                                    variant="contained"
                                    color="inherit"
                                    sx={{
                                        color: '#fff',
                                        backgroundColor: `${secondaryColor} !important`,
                                        '&:hover': { backgroundColor: `${secondaryColor} !important`, opacity: 0.9 },
                                    }}
                                >
                                    Sekundär
                                </Button>
                            </Box>
                        </Box>

                        {/* Favicon Preview */}
                        {faviconUrl && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>Favicon:</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <img
                                        src={faviconUrl.startsWith('/') ? `${process.env.REACT_APP_SSO_URL}${faviconUrl}` : faviconUrl}
                                        alt="Favicon Vorschau"
                                        style={{ width: 32, height: 32, objectFit: 'contain' }}
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).style.display = 'none';
                                        }}
                                    />
                                    <Typography variant="caption" color="text.secondary">32x32 px</Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Login Preview */}
                        {loginBackgroundUrl && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>Login-Hintergrund:</Typography>
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: 150,
                                        borderRadius: 1,
                                        backgroundImage: `url(${loginBackgroundUrl.startsWith('/') ? `${process.env.REACT_APP_SSO_URL}${loginBackgroundUrl}` : loginBackgroundUrl})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        border: '1px solid #ddd',
                                    }}
                                />
                            </Box>
                        )}

                        {/* Domain Info */}
                        {domain && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                Das Branding wird automatisch angezeigt, wenn Nutzer über <strong>{domain}</strong> auf die Plattform zugreifen.
                            </Alert>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default BrandingSettings;
